import React from "react";
import ReactDOM from "react-dom/client";

import "bootstrap/dist/js/bootstrap.min.js";
import App from "./App";
import { Provider } from "react-redux";
import store from "./redux/store";

import "./assets/css/main.scss";

import { loadGTM } from "./api/gtm";
import { loadGA } from "./api/gtag";

import reportWebVitals from "./reportWebVitals";
import { BrowserRouter } from "react-router-dom";
import axios from "axios";

// Suppress Brightcove video errors early - before any components load
// These errors come from Brightcove's iframe and cannot be fixed from our code
const originalError = console.error;
const originalWarn = console.warn;

// Global error handler to catch uncaught errors from iframes
window.addEventListener(
  "error",
  function (event) {
    const message = event.message || "";
    const source = event.filename || "";

    // Suppress errors from Brightcove iframe
    if (
      source.includes("brightcove") ||
      source.includes("videoId") ||
      source.includes("players.brightcove") ||
      message.includes("boltdns") ||
      message.includes("Mixed Content") ||
      message.includes("manifest.prod.boltdns.net")
    ) {
      event.preventDefault();
      return false;
    }
  },
  true
); // Use capture phase

console.error = function (...args) {
  try {
    const message = args[0]?.toString() || "";
    const fullMessage = args.join(" ");
    const errorObj = args[0];

    // Check if error object has boltdns URL
    const hasBoltdnsUrl =
      errorObj &&
      typeof errorObj === "object" &&
      errorObj.url &&
      typeof errorObj.url === "string" &&
      errorObj.url.includes("boltdns");

    // Check stack trace for Brightcove iframe references
    const stackTrace =
      errorObj && typeof errorObj === "object" && errorObj.stack
        ? errorObj.stack
        : "";
    const isFromBrightcoveIframe =
      stackTrace.includes("brightcove") || stackTrace.includes("videoId");

    // Suppress Brightcove mixed content errors - check multiple patterns
    const isMixedContentError =
      message.includes("Mixed Content") ||
      message.includes("mixed content") ||
      message.includes("manifest.prod.boltdns.net") ||
      message.includes("boltdns") ||
      message.includes("insecure XMLHttpRequest") ||
      message.includes("was loaded over HTTPS") ||
      message.includes("must be served over HTTPS") ||
      fullMessage.toLowerCase().includes("mixed content") ||
      fullMessage.toLowerCase().includes("boltdns") ||
      fullMessage.toLowerCase().includes("insecure xmlhttprequest") ||
      fullMessage.toLowerCase().includes("manifest.prod.boltdns") ||
      hasBoltdnsUrl ||
      (errorObj &&
        typeof errorObj === "object" &&
        errorObj.statusCode === 0 &&
        hasBoltdnsUrl) ||
      (errorObj &&
        typeof errorObj === "object" &&
        errorObj.method === "GET" &&
        hasBoltdnsUrl) ||
      isFromBrightcoveIframe;

    // Also check stringified version
    let stringifiedArgs = "";
    try {
      stringifiedArgs = JSON.stringify(args).toLowerCase();
    } catch (e) {
      // Ignore JSON stringify errors
    }

    if (
      isMixedContentError ||
      stringifiedArgs.includes("boltdns") ||
      stringifiedArgs.includes("manifest.prod.boltdns")
    ) {
      return; // Suppress Brightcove mixed content errors
    }

    // Suppress VideoJS errors
    const isVideoJSError =
      message.includes("VIDEOJS") ||
      message.includes("videojs") ||
      message.includes("VideoJS") ||
      message.includes("ProgressEvent") ||
      message.includes("Error: [object ProgressEvent]") ||
      fullMessage.toLowerCase().includes("videojs") ||
      fullMessage.toLowerCase().includes("progressevent") ||
      (errorObj &&
        typeof errorObj === "object" &&
        errorObj.message &&
        typeof errorObj.message === "string" &&
        errorObj.message.includes("ProgressEvent")) ||
      (errorObj &&
        typeof errorObj === "object" &&
        errorObj.statusCode === 0 &&
        hasBoltdnsUrl) ||
      isFromBrightcoveIframe;

    if (
      isVideoJSError ||
      stringifiedArgs.includes("videojs") ||
      stringifiedArgs.includes("progressevent")
    ) {
      return; // Suppress VideoJS errors
    }

    originalError.apply(console, args);
  } catch (e) {
    // If our suppression code fails, fall back to original
    originalError.apply(console, args);
  }
};

console.warn = function (...args) {
  try {
    const message = args[0]?.toString() || "";
    const fullMessage = args.join(" ").toLowerCase();

    // Suppress VideoJS deprecation warnings
    const isVideoJSWarning =
      message.includes("VIDEOJS") ||
      message.includes("videojs") ||
      message.includes("VideoJS") ||
      message.includes("addRemoteTextTrack") ||
      message.includes("addremotetexttrack") ||
      message.includes("manualCleanup") ||
      message.includes("manualcleanup") ||
      fullMessage.includes("videojs") ||
      fullMessage.includes("addremotetexttrack") ||
      fullMessage.includes("manualcleanup");

    if (isVideoJSWarning) {
      return; // Suppress VideoJS warnings
    }

    originalWarn.apply(console, args);
  } catch (e) {
    // If our suppression code fails, fall back to original
    originalWarn.apply(console, args);
  }
};

const root = ReactDOM.createRoot(document.getElementById("root"));
axios.defaults.baseURL = process.env.REACT_APP_API_KEY;
const gtmId = process.env.REACT_APP_GTM_ID;
const gtmAuth = process.env.REACT_APP_GTM_AUTH;

const gtmPreview = process.env.REACT_APP_GTM_PREVIEW;
if (gtmId) {
  loadGTM(gtmId, gtmAuth, gtmPreview);
}

// Initialize Google Analytics (GA4) from env
const gaMeasurementId = process.env.REACT_APP_GA4_MEASUREMENT_ID;
if (gaMeasurementId) {
  loadGA(gaMeasurementId);
} else {
  console.warn(
    "[GA4] No GA4 Measurement ID found in environment variables. GA4 will not be initialized."
  );
}

root.render(
  <React.StrictMode>
    <BrowserRouter basename="/members">
      <Provider store={store}>
        <App />
      </Provider>
    </BrowserRouter>
  </React.StrictMode>
);

reportWebVitals();
